'use client';

import styles from './shareButtons.module.scss';

export type ShareButtonsProps = {
  url: string;
  title: string;
  description?: string;
  media?: string;
};

export function ShareButtons({
  url,
  title,
  description,
  media
}: ShareButtonsProps) {
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description || '');
  const encodedMedia = encodeURIComponent(media || '');

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    reddit: `https://www.reddit.com/submit?url=${encodedUrl}&title=${encodedTitle}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedDescription}%0A%0A${encodedUrl}`,
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&media=${encodedMedia}&description=${encodedDescription}`
  };

  return (
    <div className={styles.shareButtons}>
      <h2 className={styles.title}>Share</h2>
      <div className={styles.container}>
        <a
          href={shareLinks.facebook}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.iconContainer} ${styles.facebook}`}
          aria-label="Share on Facebook"
        >
          <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
          </svg>
        </a>

        <a
          href={shareLinks.twitter}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.iconContainer} ${styles.twitter}`}
          aria-label="Share on X (Twitter)"
        >
          <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
        </a>

        <a
          href={shareLinks.reddit}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.iconContainer} ${styles.reddit}`}
          aria-label="Share on Reddit"
        >
          <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
          </svg>
        </a>

        <a
          href={shareLinks.email}
          className={`${styles.iconContainer} ${styles.email}`}
          aria-label="Share via Email"
        >
          <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline
              points="22,6 12,13 2,6"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        </a>

        <a
          href={shareLinks.pinterest}
          target="_blank"
          rel="noopener noreferrer"
          className={`${styles.iconContainer} ${styles.pinterest}`}
          aria-label="Share on Pinterest"
        >
          <svg className={styles.icon} viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 0a12 12 0 0 0-4.373 23.178c-.07-.937-.134-2.377.028-3.401.146-.926.943-3.993.943-3.993s-.24-.481-.24-1.192c0-1.117.648-1.951 1.455-1.951.687 0 1.018.515 1.018 1.133 0 .69-.44 1.722-.666 2.678-.19.802.401 1.456 1.191 1.456 1.43 0 2.529-1.507 2.529-3.683 0-1.926-1.384-3.272-3.362-3.272-2.29 0-3.634 1.717-3.634 3.494 0 .692.266 1.434.599 1.837a.24.24 0 0 1 .056.23c-.061.254-.197.802-.224.914-.035.148-.117.18-.27.108-1.006-.468-1.635-1.937-1.635-3.119 0-2.538 1.844-4.87 5.316-4.87 2.791 0 4.962 1.989 4.962 4.646 0 2.773-1.748 5.003-4.173 5.003-.815 0-1.582-.424-1.844-.925l-.502 1.912c-.181.699-.672 1.574-.998 2.108A12 12 0 1 0 12 0z" />
          </svg>
        </a>
      </div>
    </div>
  );
}
